class MindsightPDP extends HTMLElement {
  constructor() {
    super();
    this.product = null;
    this.currentVariant = null;
    this.variantMediaMap = {};
    this.moneyFormat = this.dataset.moneyFormat || '${{amount}}';
  }

  connectedCallback() {
    const json = this.querySelector('[data-product-json]');
    if (!json) return;

    this.product = JSON.parse(json.textContent);

    const mediaMapEl = this.querySelector('[data-variant-media-map]');
    if (mediaMapEl) {
      this.variantMediaMap = JSON.parse(mediaMapEl.textContent);
    }

    this.mainImage = this.querySelector('[data-main-image]');
    this.mainVideo = this.querySelector('[data-main-video]');
    this.priceEl = this.querySelector('[data-price]');
    this.variantInput = this.querySelector('[data-variant-id]');
    this.atcBtn = this.querySelector('[data-atc]');
    this.qtyEl = this.querySelector('[data-qty]');
    this.form = this.querySelector('[data-product-form]');

    this.currentVariant = this.resolveInitialVariant();

    this.thumbs = Array.from(this.querySelectorAll('[data-thumb]'));
    this.galleryCounter = this.querySelector('[data-gallery-counter]');
    this.currentThumbIndex = Math.max(0, this.thumbs.findIndex((t) => t.classList.contains('is-active')));

    this.querySelectorAll('[data-thumb]').forEach((thumb) => {
      thumb.addEventListener('click', () => this.selectThumb(thumb));
    });

    this.querySelector('[data-gallery-prev]')?.addEventListener('click', () => this.stepGallery(-1));
    this.querySelector('[data-gallery-next]')?.addEventListener('click', () => this.stepGallery(1));

    this.updateGalleryCounter();

    this.querySelectorAll('[data-variant-pill]').forEach((pill) => {
      pill.addEventListener('click', () => this.selectOption(pill));
    });

    this.querySelectorAll('[data-qty-minus]').forEach((btn) => {
      btn.addEventListener('click', () => this.changeQty(-1));
    });

    this.querySelectorAll('[data-qty-plus]').forEach((btn) => {
      btn.addEventListener('click', () => this.changeQty(1));
    });

    if (this.atcBtn) {
      this.atcBtn.addEventListener('click', (e) => this.addToCart(e));
    }

    this.querySelectorAll('[data-bundle-add]').forEach((btn) => {
      btn.addEventListener('click', (e) => {
        e.preventDefault();
        e.stopPropagation();
        this.addBundleItem(btn);
      });
    });

    this.updateUI(this.currentVariant);
    this.syncAvailability();
    this.syncOptionVisibility();
  }

  resolveInitialVariant() {
    const urlVariantId = new URL(window.location.href).searchParams.get('variant');
    if (urlVariantId) {
      const fromUrl = this.product.variants.find((v) => String(v.id) === String(urlVariantId));
      if (fromUrl?.available) return fromUrl;
    }

    const firstAvailable = this.product.variants.find((v) => v.available);
    if (firstAvailable) return firstAvailable;

    return this.product.variants.find((v) => v.id === this.product.selected_or_first_available_variant?.id)
      || this.product.selected_or_first_available_variant
      || this.product.variants[0];
  }

  selectThumb(thumb) {
    this.querySelectorAll('[data-thumb]').forEach((t) => t.classList.remove('is-active'));
    thumb.classList.add('is-active');
    const index = this.thumbs.indexOf(thumb);
    if (index >= 0) this.currentThumbIndex = index;
    this.showGalleryMedia(thumb);
    this.updateGalleryCounter();
  }

  getVideoSrc(thumb) {
    if (thumb?.dataset.videoSrc) {
      return thumb.dataset.videoSrc;
    }

    const mediaId = parseInt(thumb?.dataset.mediaId, 10);
    if (!mediaId || !this.product?.media) return null;

    const media = this.product.media.find(
      (item) => item.id === mediaId && item.media_type === 'video'
    );
    if (!media?.sources?.length) return null;

    const mp4Sources = media.sources.filter((source) => source.format === 'mp4');
    const preferred = mp4Sources.find((source) => source.height === 720) || mp4Sources[0];
    if (!preferred?.url) return null;

    return preferred.url.startsWith('//') ? `https:${preferred.url}` : preferred.url;
  }

  showGalleryMedia(thumb) {
    const videoSrc = this.getVideoSrc(thumb);
    const isVideo = thumb?.dataset.mediaType === 'video' && videoSrc;

    if (this.mainVideo) {
      if (isVideo) {
        this.mainVideo.src = videoSrc;
        this.mainVideo.hidden = false;
        this.mainVideo.load();
        this.mainVideo.play().catch(() => {});
      } else {
        this.mainVideo.pause();
        this.mainVideo.removeAttribute('src');
        this.mainVideo.hidden = true;
        this.mainVideo.load();
      }
    }

    if (this.mainImage) {
      if (isVideo) {
        this.mainImage.hidden = true;
      } else if (thumb?.dataset.fullSrc) {
        this.mainImage.hidden = false;
        this.mainImage.src = thumb.dataset.fullSrc;
        this.mainImage.removeAttribute('srcset');
      }
    }
  }

  stepGallery(delta) {
    if (!this.thumbs.length) return;
    this.currentThumbIndex = (this.currentThumbIndex + delta + this.thumbs.length) % this.thumbs.length;
    this.selectThumb(this.thumbs[this.currentThumbIndex]);
  }

  updateGalleryCounter() {
    if (!this.galleryCounter || !this.thumbs.length) return;
    this.galleryCounter.textContent = `${this.currentThumbIndex + 1} / ${this.thumbs.length}`;
  }

  selectOption(pill) {
    if (pill.classList.contains('is-disabled')) return;

    const group = pill.closest('[data-option-index]');
    const groupIndex = parseInt(group.dataset.optionIndex, 10);
    group.querySelectorAll('[data-variant-pill]').forEach((p) => p.classList.remove('is-active'));
    pill.classList.add('is-active');

    const variant = this.findVariant()
      || this.findBestVariantForOption(groupIndex, pill.dataset.optionValue);
    if (!variant) return;

    this.currentVariant = variant;
    this.updateUI(variant);
  }

  getSelectedOptions() {
    const options = [];
    this.querySelectorAll('[data-option-index]').forEach((group) => {
      const selected = group.querySelector('[data-variant-pill].is-active');
      if (selected) options.push(selected.dataset.optionValue);
    });
    return options;
  }

  findVariant() {
    const selected = this.getSelectedOptions();
    return this.product.variants.find((variant) =>
      selected.every((value, index) => variant.options[index] === value)
    );
  }

  findBestVariantForOption(optionIndex, optionValue) {
    const selected = this.getSelectedOptions();
    const candidates = this.product.variants.filter(
      (variant) => variant.options[optionIndex] === optionValue
    );
    if (!candidates.length) return null;

    const exact = candidates.find((variant) =>
      selected.every((value, index) => index === optionIndex || !value || variant.options[index] === value)
    );
    if (exact?.available) return exact;

    const available = candidates.filter((variant) => variant.available);
    if (available.length) {
      available.sort((a, b) => {
        const score = (variant) => selected.reduce(
          (total, value, index) => (
            index !== optionIndex && value && variant.options[index] === value ? total + 1 : total
          ),
          0
        );
        return score(b) - score(a);
      });
      return available[0];
    }

    return exact || candidates[0];
  }

  getVariantMedia(variant) {
    const mapped = this.variantMediaMap[String(variant.id)];
    if (mapped?.src) return mapped;

    let image = variant.featured_image;
    if (!image) {
      const styleIndex = this.getStyleOptionIndex();
      const styleValue = variant.options[styleIndex];
      const standardMatch = this.product.variants.find(
        (v) => v.options[styleIndex] === styleValue && v.options.includes('Standard') && v.featured_image
      );
      image = standardMatch?.featured_image
        || this.product.variants.find(
          (v) => v.options[styleIndex] === styleValue && v.featured_image
        )?.featured_image;
    }

    if (!image) return null;

    return {
      media_id: image.id,
      src: image.src.includes('?') ? `${image.src}&width=1200` : `${image.src}?width=1200`,
    };
  }

  getStyleOptionIndex() {
    const styleIndex = this.product.options.findIndex((name) =>
      /style|color|pattern|wood finish/i.test(name)
    );
    return styleIndex >= 0 ? styleIndex : 0;
  }

  updateUI(variant) {
    if (this.variantInput) this.variantInput.value = variant.id;
    if (this.priceEl) this.priceEl.textContent = this.formatMoney(variant.price);

    if (this.atcBtn) {
      this.atcBtn.disabled = !variant.available;
      this.atcBtn.textContent = variant.available
        ? this.atcBtn.dataset.labelAvailable || 'Add to cart'
        : 'Sold out';
    }

    this.syncPillsToVariant(variant);
    this.updateImage(variant);
    this.updateURL(variant);
    this.syncAvailability();
    this.syncOptionVisibility();
    this.dispatchEvent(
      new CustomEvent('mindsight-pdp:variant-change', {
        bubbles: true,
        detail: { variantId: variant.id, variant },
      })
    );
  }

  setVariantById(variantId) {
    const variant = this.product.variants.find((v) => String(v.id) === String(variantId));
    if (!variant) return;
    this.currentVariant = variant;
    this.updateUI(variant);
  }

  setQuantity(quantity) {
    const qty = Math.max(1, parseInt(quantity, 10) || 1);
    if (this.qtyEl) this.qtyEl.textContent = qty;
    const qtyInput = this.querySelector('[name="quantity"]');
    if (qtyInput) qtyInput.value = qty;
  }

  syncPillsToVariant(variant) {
    this.querySelectorAll('[data-option-index]').forEach((group, index) => {
      const value = variant.options[index];
      group.querySelectorAll('[data-variant-pill]').forEach((pill) => {
        pill.classList.toggle('is-active', pill.dataset.optionValue === value);
      });
    });
  }

  updateImage(variant) {
    if (!this.mainImage && !this.mainVideo) return;

    const media = this.getVariantMedia(variant);
    if (!media?.src) return;

    if (this.mainVideo) {
      this.mainVideo.pause();
      this.mainVideo.removeAttribute('src');
      this.mainVideo.hidden = true;
      this.mainVideo.load();
    }

    if (this.mainImage) {
      this.mainImage.hidden = false;
      this.mainImage.src = media.src;
      this.mainImage.alt = variant.name || this.product.title;
      this.mainImage.removeAttribute('srcset');
    }

    let matchedThumb = false;
    this.thumbs.forEach((thumb) => {
      const match = parseInt(thumb.dataset.mediaId, 10) === media.media_id;
      thumb.classList.toggle('is-active', match);
      if (match) {
        matchedThumb = true;
        this.currentThumbIndex = this.thumbs.indexOf(thumb);
        thumb.scrollIntoView({ block: 'nearest', behavior: 'smooth' });
      }
    });

    if (!matchedThumb) {
      this.querySelectorAll('[data-thumb]').forEach((t) => t.classList.remove('is-active'));
    }

    this.updateGalleryCounter();
  }

  syncAvailability() {
    const selected = this.getSelectedOptions();

    this.querySelectorAll('[data-option-index]').forEach((group, groupIndex) => {
      group.querySelectorAll('[data-variant-pill]').forEach((pill) => {
        const value = pill.dataset.optionValue;
        const hasVariant = this.product.variants.some(
          (variant) => variant.options[groupIndex] === value
        );
        const hasAvailable = this.product.variants.some(
          (variant) => variant.options[groupIndex] === value && variant.available
        );
        const fullyDisabled = !hasVariant || !hasAvailable;

        const trial = [...selected];
        trial[groupIndex] = value;
        const comboMatch = this.product.variants.find((variant) =>
          trial.every((optionValue, index) => !optionValue || variant.options[index] === optionValue)
        );
        const comboAvailable = Boolean(comboMatch?.available);

        pill.classList.toggle('is-disabled', fullyDisabled);
        pill.classList.toggle(
          'is-incompatible',
          groupIndex > 0
            && !fullyDisabled
            && !pill.classList.contains('is-active')
            && (!comboMatch || !comboAvailable)
        );
      });
    });
  }

  syncOptionVisibility() {
    const selected = this.getSelectedOptions();

    this.querySelectorAll('[data-option-index]').forEach((group, groupIndex) => {
      group.querySelectorAll('[data-variant-pill]').forEach((pill) => {
        if (groupIndex === 0) {
          pill.hidden = false;
          return;
        }

        const hasCombo = this.product.variants.some((variant) => {
          if (variant.options[groupIndex] !== pill.dataset.optionValue) return false;

          for (let i = 0; i < groupIndex; i += 1) {
            if (selected[i] && variant.options[i] !== selected[i]) return false;
          }

          return true;
        });

        pill.hidden = !hasCombo;
      });
    });
  }

  updateURL(variant) {
    if (!window.history.replaceState) return;
    const url = new URL(window.location.href);
    url.searchParams.set('variant', variant.id);
    window.history.replaceState({}, '', url.toString());
  }

  changeQty(delta) {
    if (!this.qtyEl) return;
    const current = parseInt(this.qtyEl.textContent, 10) || 1;
    const next = Math.max(1, current + delta);
    this.qtyEl.textContent = next;
    const qtyInput = this.querySelector('[name="quantity"]');
    if (qtyInput) qtyInput.value = next;
  }

  async addToCart(e) {
    e.preventDefault();
    if (!this.currentVariant?.available || !this.form) return;

    this.atcBtn.disabled = true;
    const original = this.atcBtn.textContent;
    this.atcBtn.textContent = 'Adding…';

    try {
      await this.postToCart(this.currentVariant.id, parseInt(this.qtyEl?.textContent || '1', 10));
      this.atcBtn.textContent = 'Added!';
      this.openCartDrawer();

      setTimeout(() => {
        this.atcBtn.textContent = original;
        this.atcBtn.disabled = false;
      }, 1500);
    } catch (error) {
      console.error(error);
      this.atcBtn.textContent = 'Try again';
      setTimeout(() => {
        this.atcBtn.textContent = original;
        this.atcBtn.disabled = false;
      }, 1500);
    }
  }

  async addBundleItem(btn) {
    const variantId = btn.dataset.variantId;
    if (!variantId || btn.disabled) return;

    const original = btn.textContent;
    btn.disabled = true;
    btn.textContent = 'Adding…';

    try {
      await this.postToCart(variantId, 1);
      btn.textContent = 'Added';
      this.openCartDrawer();

      setTimeout(() => {
        btn.textContent = original;
        btn.disabled = false;
      }, 1500);
    } catch (error) {
      console.error(error);
      btn.textContent = 'Try again';
      setTimeout(() => {
        btn.textContent = original;
        btn.disabled = false;
      }, 1500);
    }
  }

  async postToCart(variantId, quantity) {
    const response = await fetch(window.Shopify?.routes?.root ? `${window.Shopify.routes.root}cart/add.js` : '/cart/add.js', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
      body: JSON.stringify({ id: variantId, quantity }),
    });

    if (!response.ok) throw new Error('Add to cart failed');

    document.dispatchEvent(new CustomEvent('cart:refresh', { detail: { open: true } }));
  }

  openCartDrawer() {
    const cartDrawer = document.querySelector('cart-drawer');
    if (cartDrawer) {
      if (typeof cartDrawer.show === 'function') {
        cartDrawer.show(this.atcBtn);
        return;
      }
      if (typeof cartDrawer.open === 'function') {
        cartDrawer.open();
        return;
      }
    }

    document.querySelector('#CartDrawer')?.setAttribute('active', '');
  }

  formatMoney(cents) {
    const amount = (cents / 100).toFixed(2);
    return this.moneyFormat.replace('{{amount}}', amount).replace('{{amount_no_decimals}}', Math.round(cents / 100));
  }
}

if (!customElements.get('mindsight-pdp')) {
  customElements.define('mindsight-pdp', MindsightPDP);
}
